/**
 * Renders a single content reference field by reference type.
 * Looks up config from content reference types dictionary and wires ContentReferenceSection.
 * Shared by VideoContentReferencesSection, VideoContentReferencesExtendedSection, VideoContentReferencesYoreSection.
 * @module content-detail/content-references/ContentReferenceField
 */

import React from 'react'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'
import { SINGLE_SELECT_REFERENCE_TYPES } from '../constants'
import ContentReferenceSection from './ContentReferenceSection'
import type { ContentReference } from '../types'

export interface ContentReferenceFieldProps {
  /** Reference type key (e.g. "admin_category", "yi-yoga-teacher"). */
  referenceType: string
  /** Current references for this type. */
  value: ContentReference[]
  /** Called when selection changes. */
  onChange: (newReferences: ContentReference[]) => void
  /** Whether the field is disabled (view-only). */
  disabled: boolean
  /** Optional tooltip text; when set, an info icon after the label shows this on hover. */
  tooltip?: string
}

/**
 * Renders one content reference field. Returns null if reference type is not in dictionary.
 * Config from service dictionary uses Immutable.js (List/Map).
 */
function ContentReferenceField ({
  referenceType,
  value,
  onChange,
  disabled,
  tooltip,
}: ContentReferenceFieldProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config: any = getContentReferenceTypesContentDictionary().get(referenceType)
  if (!config) return null

  return (
    <ContentReferenceSection
      label={config.get('label')}
      value={value}
      onChange={onChange}
      disabled={disabled}
      contentType={config.get('contentType')?.first?.() || config.get('contentType')}
      bundle={config.get('bundle')}
      filterType={config.get('filterType')}
      multiple={!SINGLE_SELECT_REFERENCE_TYPES.includes(referenceType)}
      tooltip={tooltip}
    />
  )
}

export default ContentReferenceField
